import { IError } from '@/hooks/use-link-social-network/use-link-social-network.hook'
import Button from '@components/buttons/button/button.component'
import Loader from '@components/loader/loader.component'
import Modal from '@components/modals/modal/modal.component'
import DataLayerHelper from '@helpers/data-layer.helper'
import localForageHelper from '@helpers/localforage.helper'
import UserHelper from '@helpers/user.helper'
import Category from '@interfaces/category.interface'
import Environment from '@interfaces/environment.interface'
import { ModalStatusTypes, SocialNetworksAvailables, SocialNetworksResponse } from '@interfaces/socialnetworks.interface'
import User, { FacebookDiscoveryUser, Page } from '@interfaces/user.interface'
import onEnvironmentChange from '@observables/environment.observable'
import onUserChange from '@observables/user.observable'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import ConfirmVinculateComponent from './components/confirm-vinculate/confirm-vinculate.component'
import ErrorVinculateComponent from './components/error-vinculate/error-vinculate.component'
import SelectNetworkContentComponent from './components/select-network-content/select-network-content.component'
import SocialNetworksContentComponent from './components/social-networks-content/social-networks-content.component'
import SuccessVinculateComponent, { ProfileBinded } from './components/success-vinculate/success-vinculate.component'
import { Code } from './components/tutorial-instagram/hooks/use-new-tutorial-instagram/use-new-tutorial-instagram.hook'
import TutorialInstagram from './components/tutorial-instagram/tutorial-instagram.component'
import UsernameToConnect from './components/username-to-connect/username-to-connect.component'

import './modal-vinculate-social-network.scoped.scss'
import './modal-vinculate-social-network.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  openModalVinculate: boolean
  setOpenModalVinculate: (open: boolean) => void
  onCloseModal?: () => void
  back?: () => void
  socialNetwork?: SocialNetworksAvailables
  setSocialNetwork?: (socialNetwork: SocialNetworksAvailables) => void
  modalStatus?: ModalStatusTypes
  setModalStatus?: (status: ModalStatusTypes) => void
  onAddAccount?: (network: SocialNetworksAvailables) => void
  pages?: Array<Page>
  selectedProfile?: string
  setSelectedProfile?: (profile: string) => void
  stepActive?: number
  setUsername?: (username: string) => void
  loading?: boolean
  categories?: Category[]
  setCategories?: (categories: Category[]) => void
  subCategories?: Category[]
  setSubCategories?: (subCategories: Category[]) => void
  nextStep?: () => void
  userToAddOnFinish?: SocialNetworksResponse
  newUser?: User & { userInfo: User }
  error?: IError
  setError?: (error: IError) => void
}

export interface ISocialNetwork {
  title: string
  type: SocialNetworksAvailables
}

export interface State {
  header?: React.ReactNode
  stepTutorial?: number
  imageTutorialLoading?: boolean
}

export default ({
  openModalVinculate,
  setOpenModalVinculate,
  onCloseModal,
  back,
  socialNetwork = null,
  setSocialNetwork,
  modalStatus = 'initial',
  setModalStatus,
  onAddAccount,
  pages,
  selectedProfile,
  setSelectedProfile,
  stepActive,
  loading,
  nextStep,
  userToAddOnFinish,
  newUser,
  error,
  setError,
}: Props) => {
  const userHelper = useMemo(() => new UserHelper(), [])
  const dataLayerHelper = useMemo(() => new DataLayerHelper(), [])
  const [urlCreators, setUrlCreators] = useState<string>('')
  const [width, setWidth] = useState(window.innerWidth)
  const [height, setHeight] = useState(window.innerHeight)
  const [loadingProfile, setLoadingProfile] = useState(false)
  const [stepCode, setStepCode] = useState<Code>(null)
  const [skipTutorial, setSkipTutorial] = useState(false)
  const [businessProfileSelected, setBusinessProfileSelected] = useState<FacebookDiscoveryUser>(null)
  const [authUrl, setAuthUrl] = useState<string>('')
  const [action, setAction] = useState<'addAccount' | 'renew'>(null)
  const [user, setUser] = useState<User | null>(null)
  const availableSocialNetworks: ISocialNetwork[] = useMemo(() => {
    return [
      { title: 'Facebook', type: 'facebook' },
      { title: 'Twitter', type: 'twitter' },
      { title: 'Youtube', type: 'youtube' },
      { title: 'TikTok', type: 'tiktok' },
    ]
  }, [])

  const { t } = useTranslation('modalVinculateSocialNetwork')

  const profileBinded = useMemo((): ProfileBinded => {
    let identitySelected = null

    if (['instagram', 'facebook'].includes(socialNetwork) && userToAddOnFinish?.bindedProfileInfo) {
      identitySelected = userToAddOnFinish?.userInfo?.identities?.find((p) => p?.id === userToAddOnFinish?.bindedProfileInfo)
    } else if (newUser) {
      identitySelected = newUser?.identities?.find((identity) => identity.id === selectedProfile)
    }

    return {
      id: identitySelected?.id,
      username: identitySelected?.username,
      picture: identitySelected?.picture,
    }
  }, [selectedProfile, newUser, socialNetwork, userToAddOnFinish])

  const handleBack = useCallback(async () => {
    if (back && typeof back === 'function') {
      await dataLayerHelper?.sendToDataLayer({
        event: 'tutorial_action',
        scope: 'Btn - redes sociais | Facebook',
        action: 'back',
        step: 'Conexão de rede social',
      })
      back()
    }
  }, [back, dataLayerHelper])

  useEffect(() => {
    const handleSize = () => {
      setWidth(window.innerWidth)
      setHeight(window.innerHeight)
    }
    window.addEventListener('resize', handleSize)
    return () => window.removeEventListener('resize', handleSize)
  }, [])

  useEffect(() => {
    const onEnvironmentChangeSubscription: SubscriptionLike = onEnvironmentChange.subscribe((environments: Environment) => {
      setUrlCreators(environments?.squidProducts?.creators)
    })
    return () => onEnvironmentChangeSubscription?.unsubscribe()
  }, [])

  useEffect(() => {
    const init = async () => {
      const { action } = ((await localForageHelper.getItem('actions')) as { action: 'addAccount' | 'renew'; idProfile?: string }) || {}
      if (action) {
        setAction(action)
      }
    }

    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const footerImportance = useCallback(() => {
    return (
      <div style={{ width: '100%', height: 120, paddingBottom: 40 }} key="importance">
        {socialNetwork === 'facebook' ? <h6 className="title px-3">{t('actionsDescription')}</h6> : null}
        <div className="actions-footer">
          {socialNetwork === 'facebook' ? (
            <Button
              id={`social-network-skip-tutorial-${socialNetwork}`}
              width="inherit"
              size="sm"
              inverted
              color="transparent"
              borderColor="var(--primary_color)"
              textColor="var(--primary_color)"
              style={{ padding: '0.25rem 1rem' }}
              onClick={async () => {
                setSkipTutorial(true)
                await localForageHelper.removeItem('businessProfileSelected')
                await localForageHelper.setItem('isSkippedTutorial', true)
                const { action } = ((await localForageHelper.getItem('actions')) as { action: string; idProfile: string }) || {}
                await dataLayerHelper?.sendToDataLayer({
                  event: 'tutorial_action',
                  scope: 'Btn - redes sociais | Facebook',
                  action: 'skip',
                  step: 'Vamos lá | Conexão de rede social',
                })
                if (action === 'renew') {
                  window.location.href = `${authUrl}/${socialNetwork}?squid-callback=${window.location.href}`
                } else {
                  setModalStatus('profile-category')
                  onAddAccount(socialNetwork)
                  onCloseModal()
                }
              }}
              loading={loading}
            >
              <span style={{ whiteSpace: 'nowrap' }}>{t('skipTutorial')}</span>
            </Button>
          ) : (
            <Button
              id={`social-network-go-back-${socialNetwork}`}
              size="sm"
              inverted
              color="transparent"
              borderColor="var(--text_color)"
              textColor="var(--text_color)"
              style={{ padding: '0.25rem 1rem' }}
              onClick={() => handleBack()}
              loading={loading}
            >
              <span style={{ whiteSpace: 'nowrap' }}>{t('goBack')}</span>
            </Button>
          )}
          <Button
            id={`social-network-lets-go-${socialNetwork}`}
            width={socialNetwork === 'facebook' ? 'inherit' : ''}
            style={{ padding: '0.25rem 1rem' }}
            onClick={async () => {
              await dataLayerHelper?.sendToDataLayer({
                event: 'tutorial_begin',
                context: 'Conexão | Facebook',
                step: 'Conexão da rede social',
                description: 'coleta click para iniciar tutorial',
              })
              if (socialNetwork === 'facebook') {
                await dataLayerHelper?.sendToDataLayer({
                  event: 'tutorial_action',
                  scope: 'Btn - redes sociais | Facebook',
                  action: 'next',
                  step: 'Vamos lá | Conexão de rede social',
                  nextStep: 'Etapa 1',
                })
                await localForageHelper.removeItem('isSkippedTutorial')
                setModalStatus('username-to-connect')
              } else {
                setModalStatus('profile-category')
                onAddAccount(socialNetwork)
                onCloseModal()
              }
            }}
            loading={loading}
          >
            <span style={{ whiteSpace: 'nowrap' }}>{t('letsGo')}</span>
          </Button>
        </div>
      </div>
    )
  }, [onAddAccount, onCloseModal, setModalStatus, socialNetwork, loading, t, authUrl, handleBack, dataLayerHelper])

  const getId = useCallback(({ modalStatus, stepActive }) => {
    if (modalStatus === 'profile-category') {
      switch (stepActive) {
        case 1:
          return 'social-network-next-select-category'
        case 2:
          return 'social-network-next-select-sub-category'
        default:
          return 'social-network-next-select-profile'
      }
    }
    return null
  }, [])

  const footerSelectProfile = useCallback(() => {
    return (
      <div className="actions-footer" key="select-profile">
        <Button
          id="social-network-go-back-tutorial"
          size="sm"
          inverted
          color="transparent"
          borderColor="var(--text_color)"
          textColor="var(--text_color)"
          style={{ padding: '0.25rem 1rem' }}
          onClick={async () => {
            await dataLayerHelper?.sendToDataLayer({
              event: 'tutorial_action',
              scope: 'Btn - redes sociais | Facebook',
              action: 'back',
              step: 'select-profile',
            })
            back()
          }}
          loading={loading}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{t('goBack')}</span>
        </Button>
        <Button
          id={getId({ modalStatus, stepActive })}
          style={{ padding: '0.25rem 1rem' }}
          disabled={selectedProfile === null}
          loading={loading}
          onClick={async () => {
            await dataLayerHelper?.sendToDataLayer({
              event: 'tutorial_action',
              scope: 'Btn - redes sociais | Facebook',
              action: 'next',
              step: 'select-profile',
              nextStep: 'input_username',
            })
            nextStep()
            try {
              const action = await localForageHelper.getItem('actions')
              const actionType = action === 'renew' ? 'renew' : 'addAccount'
              await dataLayerHelper?.sendToDataLayer({
                event: 'connection_social_network',
                socialNetwork: socialNetwork,
                step: 'Validação de conexão',
                profile: selectedProfile,
                return: `sucesso | ${actionType}`,
                description: 'coleta return de conexão',
              })
            } catch (error) {
              await dataLayerHelper?.sendToDataLayer({
                event: 'connection_social_network',
                socialNetwork: socialNetwork,
                step: 'Validação de conexão',
                profile: selectedProfile,
                return: 'erro',
                description: error.message,
              })
            }
          }}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{t('toConnect')}</span>
        </Button>
      </div>
    )
  }, [back, modalStatus, nextStep, stepActive, loading, t, getId, selectedProfile, dataLayerHelper, socialNetwork])

  const footerConfirmVinculate = useCallback(() => {
    return (
      <div className="actions-footer" key="confirm-vinculate">
        <Button
          id="social-network-go-back-tutorial"
          size="sm"
          inverted
          color="transparent"
          borderColor="var(--text_color)"
          textColor="var(--text_color)"
          style={{ padding: '0.25rem 1rem' }}
          loading={loading}
          onClick={async () => {
            await dataLayerHelper?.sendToDataLayer({
              event: 'tutorial_action',
              scope: 'Btn - redes sociais | Facebook',
              action: 'back',
              step: 'confirm-vinculate',
              nextStep: 'tutorial-instagram',
            })
            setModalStatus('tutorial-instagram')
          }}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{t('goBack')}</span>
        </Button>
        <Button
          id={'socialNetwork_next_confirm'}
          style={{ padding: '0.25rem 1rem' }}
          loading={loading}
          onClick={async () => {
            await dataLayerHelper?.sendToDataLayer({
              event: 'tutorial_action',
              scope: 'Btn - redes sociais | Facebook',
              action: 'next',
              step: 'confirm-vinculate',
              nextStep: 'profile-category',
            })
            setModalStatus('profile-category')
            onAddAccount(socialNetwork)
            onCloseModal()
          }}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{t('next')}</span>
        </Button>
      </div>
    )
  }, [t, setModalStatus, onAddAccount, onCloseModal, socialNetwork, loading, dataLayerHelper])

  const businessProfileSelectedAlreadyLinked = useCallback(
    (idProfile: string) => !!user?.identities?.find(({ id }) => id === idProfile),
    [user?.identities]
  )

  const handleOpenTutorial = useCallback(
    (stepCode: Code) => {
      setStepCode(stepCode)
      setModalStatus('tutorial-instagram')
      setOpenModalVinculate(true)
    },
    [setModalStatus, setOpenModalVinculate]
  )

  const handleNextStep = useCallback(async () => {
    await dataLayerHelper?.sendToDataLayer({
      event: 'tutorial_action',
      scope: 'Btn - redes sociais | Facebook',
      action: 'next',
      step: 'input_username',
      nextStep: 'tutorialInstagram',
    })
    if (skipTutorial) {
      setModalStatus('profile-category')
      onAddAccount(socialNetwork)
      onCloseModal()
    } else {
      let { action } = ((await localForageHelper.getItem('actions')) as { action: 'addAccount' | 'renew'; idProfile?: string }) || {}
      action = action || 'addAccount'

      setAction(action)

      await localForageHelper.setItem('businessProfileSelected', businessProfileSelected)
      if (action === 'addAccount') {
        if (businessProfileSelectedAlreadyLinked(businessProfileSelected?.id)) {
          setModalStatus('error')
          setError({ message: 'profileAlreadyLinked' })
        } else {
          handleOpenTutorial('HAS_FACEBOOK_PAGE')
        }
      } else if (action === 'renew') {
        setModalStatus('tutorial-instagram')
      }
    }
  }, [
    handleOpenTutorial,
    setModalStatus,
    businessProfileSelected,
    onAddAccount,
    onCloseModal,
    skipTutorial,
    socialNetwork,
    businessProfileSelectedAlreadyLinked,
    setError,
    dataLayerHelper,
  ])

  const footerUsernameToConnect = useCallback(() => {
    return (
      <div className="actions-footer" key="username-to-connect">
        <Button
          id="social-network-go-back-tutorial"
          size="sm"
          inverted
          borderColor="var(--primary_color)"
          textColor="var(--primary_color)"
          style={{ padding: '0.25rem 1rem' }}
          loading={loading || loadingProfile}
          disabled={loadingProfile}
          onClick={async () => {
            await dataLayerHelper?.sendToDataLayer({
              event: 'tutorial_action',
              scope: 'Btn - redes sociais | Facebook',
              action: 'Voltar',
              step: 'input_username',
              nextStep: 'select-social-network',
            })
            setModalStatus('initial')
          }}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{t('goBack')}</span>
        </Button>
        <Button
          id={'socialNetwork_next_confirm'}
          style={{ padding: '0.25rem 1rem' }}
          loading={loadingProfile}
          disabled={!businessProfileSelected || loadingProfile}
          onClick={async () => await handleNextStep()}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{t('next')}</span>
        </Button>
      </div>
    )
  }, [loading, loadingProfile, setModalStatus, t, handleNextStep, businessProfileSelected, dataLayerHelper])

  const footerAndHeaderConfigMap = useMemo(
    () => ({
      initial: { footer: footerImportance, header: <h5 className="m-0">{t('socialNetworkLink')}</h5> },
      'profile-category': { footer: footerSelectProfile, header: <h5 className="m-0">{t('socialNetworkLink')}</h5> },
      'tutorial-instagram': { footer: () => null, header: null },
      'username-to-connect': {
        footer: footerUsernameToConnect,
        header: <h5 className="m-0">{t('usernameToConnect.title')}</h5>,
      },
      'confirm-vinculate': { footer: footerConfirmVinculate, header: <h5 className="m-0">{t('confirmVinculateTitle')}</h5> },
      success: { footer: () => null, header: null },
    }),
    [footerConfirmVinculate, footerImportance, footerSelectProfile, t, footerUsernameToConnect]
  )

  const getFooter = useCallback(() => {
    if (!socialNetwork) return null
    return footerAndHeaderConfigMap?.[modalStatus]?.footer()
  }, [modalStatus, socialNetwork, footerAndHeaderConfigMap])

  const getHeader = useCallback(() => {
    return footerAndHeaderConfigMap?.[modalStatus]?.header
  }, [modalStatus, footerAndHeaderConfigMap])

  const handleCloseModal = useCallback(async () => {
    if (onCloseModal && typeof onCloseModal === 'function') {
      if (userToAddOnFinish) {
        await userHelper.setUserObject(userToAddOnFinish?.userInfo, userToAddOnFinish?.JWT, userToAddOnFinish?.expireDate)
        await userHelper.setUser({
          ...user,
          ...userToAddOnFinish?.userInfo,
          ...profileBinded,
          statusProviders: userToAddOnFinish?.statusProviders,
        })
        setUser({
          ...user,
          ...userToAddOnFinish?.userInfo,
          ...profileBinded,
          statusProviders: userToAddOnFinish?.statusProviders,
        })
      } else if (newUser) {
        await userHelper.setUser({ ...user, ...newUser?.userInfo })
      }
      setStepCode(null)
      await dataLayerHelper?.sendToDataLayer({
        event: 'tutorial_action',
        scope: 'Btn - redes sociais | Facebook',
        action: 'abandoned_close',
        step: 'Conexão de rede social',
      })
      onCloseModal()
    }
  }, [onCloseModal, newUser, user, userHelper, userToAddOnFinish, setUser, dataLayerHelper, profileBinded])

  const handleBusinessSelectedProfile = useCallback(
    (businessProfile: FacebookDiscoveryUser) => {
      setBusinessProfileSelected(businessProfile)
    },
    [setBusinessProfileSelected]
  )

  useEffect(() => {
    if (!['initial', 'username-to-connect']?.includes(modalStatus)) {
      setSkipTutorial(false)
    }
  }, [modalStatus])

  useEffect(() => {
    const onEnvironmentChangeSubscription: SubscriptionLike = onEnvironmentChange.subscribe((environment: Environment) => {
      setAuthUrl(environment?.squidApis?.proxy)
    })

    return () => onEnvironmentChangeSubscription?.unsubscribe()
  }, [])

  useEffect(() => {
    const onUserChangeSubscription: SubscriptionLike = onUserChange.subscribe((user: User) => {
      setUser(user)
    })

    return () => onUserChangeSubscription?.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Modal
      forceMobileNoMargin
      modalSize={stepActive === 1 || stepActive === 2 || window.innerWidth < 991 ? 'lg' : 'sm'}
      open={openModalVinculate}
      className="modal-social-network"
      header={getHeader()}
      footer={getFooter()}
      onOpenChange={(open: boolean) => setOpenModalVinculate(open)}
      onCloseChange={() => handleCloseModal()}
      buttonClose={modalStatus !== 'tutorial-instagram'}
    >
      {loading && (
        <div
          className="loader-modal"
          style={{
            height: width < 991 ? `calc(${height}px - 30px - 55px - 61px)` : null,
          }}
        >
          <Loader />
          <p className="pt-5 text-center" dangerouslySetInnerHTML={{ __html: t('loadingMessage') }} />
        </div>
      )}
      {modalStatus === 'initial' && !loading && (
        <SelectNetworkContentComponent
          availableSocialNetworks={availableSocialNetworks}
          height={height}
          width={width}
          socialNetwork={socialNetwork}
          setSocialNetwork={setSocialNetwork}
        />
      )}
      {modalStatus === 'profile-category' && !loading && (
        <SocialNetworksContentComponent
          height={height}
          width={width}
          loading={loading}
          pages={pages}
          selectedProfile={selectedProfile}
          setSelectedProfile={setSelectedProfile}
          socialNetwork={socialNetwork}
          stepActive={stepActive}
        />
      )}
      {modalStatus === 'tutorial-instagram' && !loading && (
        <TutorialInstagram
          onCloseModal={onCloseModal}
          onSetModalStatus={(modalStatus) => setModalStatus(modalStatus)}
          onAddAccount={onAddAccount}
          initialStepCode={stepCode}
          notBusinessProfile={action === 'renew' && !businessProfileSelected}
        />
      )}
      {modalStatus === 'username-to-connect' && !loading && (
        <UsernameToConnect
          onOpenTutorial={(stepCode: Code) => handleOpenTutorial(stepCode)}
          onBusinessProfileSelected={handleBusinessSelectedProfile}
          businessProfileSelected={businessProfileSelected}
          onLoadProfile={setLoadingProfile}
          setModalStatus={setModalStatus}
          height={height}
        />
      )}
      {modalStatus === 'confirm-vinculate' && !loading && (
        <ConfirmVinculateComponent height={height} width={width} urlCreators={urlCreators} />
      )}
      {modalStatus === 'success' && !loading && (
        <SuccessVinculateComponent
          height={height}
          width={width}
          onCloseModal={async () => await handleCloseModal()}
          profileBinded={profileBinded}
          socialNetwork={socialNetwork}
          user={user}
        />
      )}
      {modalStatus === 'error' && !loading && (
        <ErrorVinculateComponent
          height={height}
          width={width}
          onCloseModal={() => handleCloseModal()}
          error={error}
          setError={setError}
          onOpenTutorial={(stepCode) => handleOpenTutorial(stepCode)}
          setModalStatus={setModalStatus}
          user={user}
          socialNetwork={socialNetwork}
        />
      )}
    </Modal>
  )
}
